import type { Signal, WritableSignal } from '@angular/core';
import { Injectable, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, tap } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';

import type { AppIdentity } from '@evc/platform';
import type { Menu, MenuItemType } from '@evc/web-components';
import { BtnActionEnum } from '@evc/web-components';

@Injectable({
  providedIn: 'root',
})
export class NavConfigService {
  #appIdentity!: WritableSignal<AppIdentity>;
  #helpMenu!: WritableSignal<MenuItemType[]>;
  #settingsMenu!: WritableSignal<MenuItemType[]>;
  #leftbarConfig!: WritableSignal<Menu[]>;
  #topTabs!: Signal<MenuItemType[]>;

  #router: Router = inject(Router);

  constructor() {
    this.initConfig();

    this.#router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntilDestroyed(),
      tap(() => this.updateSelectedMenuItem())
    ).subscribe();

  }

  private initConfig(): void {
    this.#leftbarConfig = signal<Menu[]>([
      {
        items: [  // This line creates the first section in the left bar
          {
            text: 'Public page', // Temporary text until i18n is set up
            key: '/',
            icon: 'home',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/'),
            },
          },
          {
            text: 'Private page', // Temporary text until i18n is set up
            key: '/private',
            icon: 'security-lock',
            selected: false,
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: () => this.#router.navigateByUrl('/private'),
            },
          },
        ],
      }
    ]);

    this.#appIdentity = signal<AppIdentity>({
      title: 'Demo App',                                         // Temporary title until i18n is set up
      //logo: '/assets/logo.svg#demoapp',                                  // This logo is local to this project ; you may use one of our library's application icons, as shown below
      logo: '/assets/applications/generic-app.svg#generic-app',        // For logo pulled from the @evc/web-components library
      version: "Version 42",                                     // Version your app as desired. Can be dynamic or static.
    });


    this.#helpMenu = signal<MenuItemType[]>(
      (['help_and_support', 'release_notes', 'terms_and_conditions', 'platform_status'])
      .map(key => ({
        key,
        text: key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()), // Temporary text until i18n is set up
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: `https://example.com/${key}`, // Temporary URL until i18n is set up
        },
      })) as MenuItemType[],
    );

    this.#settingsMenu = signal<MenuItemType[]>(
      (['language', 'notifications'])
      .map(key => ({
        key,
        text: key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()), // Temporary text until i18n is set up
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: `https://example.com/${key}`, // Temporary URL until i18n is set up
        },
      })) as MenuItemType[],
    );
  }

  public updateSelectedMenuItem(): void {
    if (!this.#leftbarConfig) return;

    const currentUrl = this.#router.url;

    this.#leftbarConfig.update((config) =>
      config.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          selected:
            item.key === currentUrl,
        })),
      })),
    );
  }

  public get appIdentity(): WritableSignal<AppIdentity> {
    return this.#appIdentity;
  }

  public get helpMenu(): WritableSignal<MenuItemType[]> {
    return this.#helpMenu;
  }

  public get leftbarConfig(): WritableSignal<Menu[]> {
    return this.#leftbarConfig;
  }

  public set leftbarConfig(value: Menu[]) {
    this.#leftbarConfig.set(value);
  }

    public get settingsMenu(): WritableSignal<MenuItemType[]> {
    return this.#settingsMenu;
  }

  public get topTabs(): Signal<MenuItemType[]> {
    return this.#topTabs;
  }
}
